import { useEffect, useState } from 'react';

import { useGetScreeningCases } from '../../../api/created/screening.ts';

const DEBOUNCE_MS = 300;
const DEFAULT_PAGE_SIZE = 10;

export function useScreeningCasesTable() {
  const [page, setPage] = useState(0);
  const size = DEFAULT_PAGE_SIZE;
  const [activeOnly, setActiveOnly] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [search]);

  const { data, isLoading, isFetching } = useGetScreeningCases({
    activeOnly,
    page,
    size,
    search: debouncedSearch || undefined,
  });

  const cases = data?.content ?? [];
  const totalCount = data?.totalCount ?? 0;
  const currentPage = data?.page ?? page;
  const currentSize = data?.size ?? size;
  const totalPages = currentSize > 0 ? Math.ceil(totalCount / currentSize) : 0;

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(0);
  };

  const handleActiveOnlyChange = (value: boolean) => {
    setActiveOnly(value);
    setPage(0);
  };

  const handlePrevPage = () => setPage((prev) => Math.max(0, prev - 1));
  const handleNextPage = () => setPage((prev) => Math.min(Math.max(0, totalPages - 1), prev + 1));

  return {
    cases,
    isLoading: isLoading || isFetching,
    page: currentPage,
    totalPages,
    activeOnly,
    search,
    handleActiveOnlyChange,
    handleSearchChange,
    handlePrevPage,
    handleNextPage,
  };
}
